import React, { memo, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_COMPARE_DASH,
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  chartBaseTooltip,
  chartFaded,
  chartIntegerTicks,
  chartLegend,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

const DASH = '—';

export interface IMultiLineSeries {
  /** Ключ ряда — уходит в `onSelect`. */
  id: string;
  label: string;
  /** По точкам оси; `null` — разрыв (не ноль). */
  data: ReadonlyArray<number | null>;
  color: string;
  /** Пунктир: сравнение, незрелые точки, конкурент. */
  dashed?: boolean;
  /** Жирная линия: «мы» среди конкурентов. */
  emphasis?: boolean;
  /**
   * Неполные точки (текущая неделя, первая корзина окна не с понедельника):
   * точка полая и бледная, отрезок к ней — пунктиром (финальная проверка
   * 0103, F-E5). Не задано — все точки полные.
   */
  partial?: ReadonlyArray<boolean>;
  /**
   * ТЗ 0113: заливка под линией (area). В стеке — до ряда ниже, иначе — до
   * нуля.
   */
  fill?: boolean;
}

/** Отрезок chart.js: номера точек его концов (scriptable `segment`). */
interface IMultiLineSegmentContext {
  p0DataIndex: number;
  p1DataIndex: number;
}

export interface IMultiLineDataset {
  label: string;
  data: Array<number | null>;
  borderColor: string;
  backgroundColor: string;
  borderWidth: number;
  borderDash?: number[];
  pointRadius: number;
  /** По точкам: у неполной — белая заливка (полая точка). */
  pointBackgroundColor: string[];
  /** По точкам: у неполной — бледная обводка. */
  pointBorderColor: string[];
  tension: number;
  spanGaps: false;
  /** Area: `origin` — до нуля, `-1` — до предыдущего ряда стека. */
  fill?: 'origin' | '-1' | false;
  /** Отрезок к неполной точке (или от неё) — пунктир и бледнее. */
  segment?: {
    borderDash: (ctx: IMultiLineSegmentContext) => number[] | undefined;
    borderColor: (ctx: IMultiLineSegmentContext) => string | undefined;
  };
}

const PARTIAL_POINT_FILL = '#ffffff';

/** Ряды → датасеты chart.js (чистый построитель). */
export const multiLineData = (
  labels: ReadonlyArray<string>,
  series: ReadonlyArray<IMultiLineSeries>,
  { stacked = false }: { stacked?: boolean } = {},
): { labels: string[]; datasets: IMultiLineDataset[] } => ({
  labels: [...labels],
  datasets: series.map((item, seriesIndex) => {
    const partialAt = (index: number) => Boolean(item.partial?.[index]);
    const anyPartial = labels.some((_, index) => partialAt(index));
    const touchesPartial = (ctx: IMultiLineSegmentContext) =>
      partialAt(ctx.p0DataIndex) || partialAt(ctx.p1DataIndex);
    return {
      label: item.label,
      data: labels.map((_, index) => item.data[index] ?? null),
      borderColor: item.color,
      backgroundColor: item.fill ? chartFaded(item.color, 0.25) : item.color,
      ...(item.fill
        ? { fill: stacked && seriesIndex > 0 ? ('-1' as const) : 'origin' }
        : {}),
      borderWidth: item.emphasis ? 3 : 1.75,
      borderDash: item.dashed ? CHART_COMPARE_DASH : undefined,
      pointRadius: item.emphasis ? 3 : 2,
      pointBackgroundColor: labels.map((_, index) =>
        partialAt(index) ? PARTIAL_POINT_FILL : item.color,
      ),
      pointBorderColor: labels.map((_, index) =>
        partialAt(index) ? chartFaded(item.color, 0.55) : item.color,
      ),
      // Без сглаживания (пакет 2): кривая между точками рисовала значения,
      // которых не было.
      tension: 0,
      spanGaps: false,
      ...(anyPartial
        ? {
            segment: {
              borderDash: (ctx: IMultiLineSegmentContext) =>
                touchesPartial(ctx) ? CHART_COMPARE_DASH : undefined,
              borderColor: (ctx: IMultiLineSegmentContext) =>
                touchesPartial(ctx) ? chartFaded(item.color, 0.55) : undefined,
            },
          }
        : {}),
    };
  }),
});

export interface IMultiLineChartProps {
  labels: ReadonlyArray<string>;
  series: ReadonlyArray<IMultiLineSeries>;
  format: (value: number) => string;
  /** Полные подписи точек в подсказке. */
  titles?: ReadonlyArray<string>;
  /**
   * Вторая строка заголовка подсказки по точкам («неполная: по 10.10»);
   * `null` — без неё.
   */
  notes?: ReadonlyArray<string | null>;
  /** Верх оси (100 для процентов); не задан — по данным. */
  max?: number;
  /** Ось вниз: меньше — лучше (средняя позиция). */
  reverse?: boolean;
  /** Клик по точке ряда: id ряда и номер точки. */
  onSelect?: (seriesId: string, index: number) => void;
  ariaLabel?: string;
  /** Счётчик или позиция: только целые отметки оси. */
  integer?: boolean;
  /** Шаг отметок (позиции в выдаче — ровно 1). */
  stepSize?: number;
  /** ТЗ 0113: ряды стеком (area со стеком). */
  stacked?: boolean;
}

/**
 * ТЗ 0103 — несколько линий на одной оси (обобщённый `TrendChart` аналитики
 * опросов): видимость по неделям, конверсия когорт по источникам. Легенда
 * переключает ряды (стандарт chart.js), пунктир — сравнение или незрелое.
 */
export const MultiLineChart = memo(
  ({
    labels,
    series,
    format,
    titles,
    notes,
    max,
    reverse = false,
    onSelect,
    ariaLabel,
    integer = false,
    stepSize,
    stacked = false,
  }: IMultiLineChartProps) => {
    const data = useMemo(() => multiLineData(labels, series, { stacked }), [
      labels,
      series,
      stacked,
    ]);
    const options: ChartOptions<'line'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        spanGaps: false,
        interaction: { mode: 'index', intersect: false },
        onClick: (_event, elements) => {
          const [first] = elements;
          const item = first ? series[first.datasetIndex] : null;
          if (first && item && onSelect) onSelect(item.id, first.index);
        },
        plugins: {
          legend: { ...chartLegend, display: series.length > 1 },
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              title: (items) => {
                const [first] = items;
                if (!first) return '';
                return titles?.[first.dataIndex] ?? String(first.label ?? '');
              },
              afterTitle: (items) => {
                const [first] = items;
                return (first && notes?.[first.dataIndex]) || '';
              },
              // Квадратик ряда — цветом линии: у неполной (полой) точки
              // заливка белая, и квадратик был бы пустым.
              labelColor: (item) => {
                const color = String(item.dataset.borderColor);
                return { borderColor: color, backgroundColor: color };
              },
              label: (item) => {
                const raw = item.raw as number | null;
                return `${item.dataset.label}: ${
                  raw === null ? DASH : format(Number(raw))
                }`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 16,
              font: CHART_TICK_FONT,
              color: CHART_INK,
            },
          },
          y: {
            stacked,
            reverse,
            beginAtZero: !reverse,
            max,
            grid: { color: CHART_GRID },
            ticks: {
              font: CHART_TICK_FONT,
              color: CHART_INK,
              ...chartIntegerTicks(integer, stepSize),
              callback: (value) => format(Number(value)),
            },
          },
        },
      }),
      [
        series,
        format,
        titles,
        notes,
        max,
        reverse,
        onSelect,
        integer,
        stepSize,
        stacked,
      ],
    );
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart type="line" data={data as never} options={options as never} />
      </C.ChartBox>
    );
  },
);

MultiLineChart.displayName = 'MultiLineChart';
